import { RunTimeProps } from '@cloudio-saas/datasource-types';
import {
  StateXSetter,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  AnyColumn,
  AnyFormulaColumn,
  DataGrid,
  Item,
  Page,
  Platform,
} from 'componentregistry';
import { TreeColumnItem } from 'componentregistry/components/DataGrid/columns/TreeColumn/TreeColumn';
import { useTreeState } from 'components/DataGrid/columns/TreeColumn/useTreeState';

import {
  gridSmartSearchAtom,
  smartSearchUpdateAtom,
} from 'components/SmartSearch/state';
import {
  childrenRTAtom,
  designPath,
  smartSearchChildrenRTAtom,
  useDeveloperMode,
} from 'designer/atoms';
import { getChildren, setFormulaItems } from 'designer/state';
import {
  useParentItemId,
  usePlatform,
  useVisibleChildren,
} from 'platform/PlatformHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createItem, useVisibleChildItems } from 'registry/Components';
import useAppStore from 'store/useAppStore';
import { isNotEmpty } from 'utils/isEmpty';

const EMPTY_ARRAY: AnyColumn[] = [];

export interface ColumnGroup {
  fromColumnIndex: number;
  label: string;
  rowIndex: number;
  toColumnIndex: number;
  width: number;
  left?: number;
  groupStart?: boolean;
  groupEnd?: boolean;
  itemIds: string[];
}

export const useColumns = ({
  appUid,
  pageId,
  itemId,
  platform,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  platform: Platform;
}) => {
  const set = useStateXSetter();
  const get = useStateXGetter();
  const { formulaColumnsRecord, isQueried } = useFormulaColumns(
    appUid,
    pageId,
    itemId,
  );

  useEffect(() => {
    if (isQueried) {
      setFormulaItems(
        appUid,
        pageId,
        itemId,
        formulaColumnsRecord?.runtimeItems ?? {},
        formulaColumnsRecord?.runtimeChildren ?? [],
        formulaColumnsRecord?.runtimeProps ?? {},
        set,
        get,
      );
    }
  }, [appUid, formulaColumnsRecord, get, isQueried, itemId, pageId, set]);

  const getColumnWidth = (column: AnyColumn): number => {
    const width = column.props['width'];
    return width && typeof width === 'number' ? +width : 200;
  };

  const getColumnHeader = useCallback(
    (column: AnyColumn, headerIndex: number): string | null | undefined => {
      let header: string | null | undefined = null;
      switch (headerIndex) {
        case 1:
          header = platform.evalIfExpressionString(
            appUid,
            pageId,
            column.itemId,
            'header1',
            {},
            column.props.header1,
          );
          break;
        case 2:
          header = platform.evalIfExpressionString(
            appUid,
            pageId,
            column.itemId,
            'header2',
            {},
            column.props.header2,
          );
          break;
        case 3:
          header = platform.evalIfExpressionString(
            appUid,
            pageId,
            column.itemId,
            'header3',
            {},
            column.props.header3,
          );
          break;
        default:
          header = null;
      }
      return header;
    },
    [appUid, pageId, platform],
  );

  const initColumnGroups = useCallback(
    (columns: AnyColumn[], headerIndex: number): Array<ColumnGroup> => {
      const groups: Array<ColumnGroup> = [];
      let group: ColumnGroup | null | undefined;
      let header: string | null | undefined;
      columns.forEach((column, index) => {
        header = getColumnHeader(column, headerIndex);
        if (header && isNotEmpty(header)) {
          if (!group || group.label !== header) {
            group = {
              fromColumnIndex: index,
              toColumnIndex: index,
              width: 0,
              label: header.trim(),
              rowIndex: headerIndex - 1,
              groupStart: !group,
              itemIds: [],
            };
            groups.push(group);
          }
          group.toColumnIndex = index;
          group.width += getColumnWidth(column);
          group.rowIndex = headerIndex - 1;
          group.itemIds = [...group.itemIds, column.itemId];
        } else {
          if (group) {
            group.groupEnd = true;
          }
          group = null;
        }
        return true;
      });
      return groups;
    },
    [getColumnHeader],
  );

  const _items = useVisibleChildItems(appUid, pageId, itemId);
  const items = useMemo(
    () =>
      _items.filter(
        (item) =>
          item.type !== 'TriggerOnRowClick' &&
          item.type !== 'TriggerOnRowDoubleClick',
      ) as AnyColumn[],
    [_items],
  );
  const parentItemId = useParentItemId(appUid, pageId, itemId);
  const parentItem = parentItemId
    ? platform.getItem(appUid, pageId, parentItemId)
    : undefined;

  // console.log(parentItem);
  const {
    columns,
    leftColumns,
    rightColumns,
    ids,
    footerRows,
    leftFixedColumnGroups,
    bodyColumnGroups,
    rightFixedColumnGroups,
    h1LeftFixedColumnGroups,
    h1BodyColumnGroups,
    h1RightFixedColumnGroups,
    h2LeftFixedColumnGroups,
    h2BodyColumnGroups,
    h2RightFixedColumnGroups,
    h3LeftFixedColumnGroups,
    h3BodyColumnGroups,
    h3RightFixedColumnGroups,
    groupColumns,
    h1GroupColumns,
    h2GroupColumns,
    h3GroupColumns,
    leftGroupColumns,
    rightGroupColumns,
    bodyGroupColumns,
    h1LeftColumns,
    h1RightColumns,
    h1BodyColumns,
    h2LeftColumns,
    h2RightColumns,
    h2BodyColumns,
    h3LeftColumns,
    h3RightColumns,
    h3BodyColumns,
  } = useMemo(() => {
    const __items = items;
    const hasPinLeft = __items.some((c) => c.props.pinLeft);
    const _leftColumns = hasPinLeft
      ? __items.filter(
          (c) =>
            c.props.pinLeft ||
            ['RowNumberColumn', 'RowSelectionColumn'].includes(c.type),
        )
      : EMPTY_ARRAY;
    const hasPinRight = __items.some((c) => c.props.pinRight);
    const _rightColumns = hasPinRight
      ? __items.filter((c) => c.props.pinRight)
      : EMPTY_ARRAY;
    const bodyColumns =
      hasPinLeft || hasPinRight
        ? __items.filter((c) => !(_leftColumns.includes(c) || c.props.pinRight))
        : __items;
    if (
      parentItem?.type === 'MutliSelectLOVField' ||
      parentItem?.type === 'MultiSelectLOVColumn'
    ) {
      let page = get(designPath({ appUid, pageId })) as Page;
      page = { ...page };
      const item = platform.getItem(appUid, pageId, itemId);
      const present = item?.children.some((id) => {
        const childItem = platform.getItem(appUid, pageId, id);
        return childItem?.type === 'RowSelectionColumn';
      });
      if (!present) {
        let newItem: Item<'RowSelectionColumn'> | null = null;
        ({ page, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'RowSelectionColumn',
          appUid,
          pageId,
        }));
        set(designPath({ appUid, pageId }), page);
        newItem.props.width = 50;
        newItem.props.pinLeft = true;
      }
    }
    const page = platform.getPage(appUid, pageId);
    const _footerRows = __items.reduce((acc, c) => {
      const count = c.children.filter(
        (child) => page[child]?.type === 'ColumnFooter',
      ).length;
      if (count > acc) {
        acc = count;
      }
      return acc;
    }, 0);

    const _columns = [..._leftColumns, ...bodyColumns, ..._rightColumns];

    const _leftFixedColumnGroups: ColumnGroup[][] = [];
    let groupIndex = 1;
    let groups = initColumnGroups(_leftColumns, groupIndex);
    while (groupIndex < 4) {
      if (groups.length) _leftFixedColumnGroups.push(groups);
      groups = initColumnGroups(_leftColumns, ++groupIndex);
    }

    const _h1LeftFixedColumnGroups: ColumnGroup[][] = [];
    const _h2LeftFixedColumnGroups: ColumnGroup[][] = [];
    const _h3LeftFixedColumnGroups: ColumnGroup[][] = [];
    groupIndex = 1;
    groups = initColumnGroups(_leftColumns, groupIndex);
    if (groups.length) _h1LeftFixedColumnGroups.push(groups);
    while (groupIndex < 3) {
      groups = initColumnGroups(_leftColumns, ++groupIndex);
      if (groupIndex === 2 && groups.length)
        _h2LeftFixedColumnGroups.push(groups);
      if (groupIndex === 3 && groups.length)
        _h3LeftFixedColumnGroups.push(groups);
    }

    const _bodyColumnGroups: ColumnGroup[][] = [];
    groupIndex = 1;
    groups = initColumnGroups(bodyColumns, groupIndex);
    while (groupIndex < 4) {
      if (groups.length) _bodyColumnGroups.push(groups);
      groups = initColumnGroups(bodyColumns, ++groupIndex);
    }

    const _h1BodyColumnGroups: ColumnGroup[][] = [];
    const _h2BodyColumnGroups: ColumnGroup[][] = [];
    const _h3BodyColumnGroups: ColumnGroup[][] = [];
    groupIndex = 1;
    groups = initColumnGroups(bodyColumns, groupIndex);
    if (groups.length) _h1BodyColumnGroups.push(groups);
    while (groupIndex < 3) {
      groups = initColumnGroups(bodyColumns, ++groupIndex);
      if (groupIndex === 2 && groups.length) _h2BodyColumnGroups.push(groups);
      if (groupIndex === 3 && groups.length) _h3BodyColumnGroups.push(groups);
    }

    const _rightFixedColumnGroups: ColumnGroup[][] = [];
    groupIndex = 1;
    groups = initColumnGroups(_rightColumns, groupIndex);
    while (groupIndex < 4) {
      if (groups.length) _rightFixedColumnGroups.push(groups);
      groups = initColumnGroups(_rightColumns, ++groupIndex);
    }

    const _h1RightFixedColumnGroups: ColumnGroup[][] = [];
    const _h2RightFixedColumnGroups: ColumnGroup[][] = [];
    const _h3RightFixedColumnGroups: ColumnGroup[][] = [];
    groupIndex = 1;
    groups = initColumnGroups(_rightColumns, groupIndex);
    if (groups.length) _h1RightFixedColumnGroups.push(groups);
    while (groupIndex < 3) {
      groups = initColumnGroups(_rightColumns, ++groupIndex);
      if (groupIndex === 2 && groups.length)
        _h2RightFixedColumnGroups.push(groups);
      if (groupIndex === 3 && groups.length)
        _h3RightFixedColumnGroups.push(groups);
    }

    const skipColumns: string[] = [];
    _leftFixedColumnGroups?.forEach((lfc) => {
      lfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        skipColumns.push(..._ids);
      });
    });
    _bodyColumnGroups?.forEach((bc) => {
      bc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        skipColumns.push(..._ids);
      });
    });
    _rightFixedColumnGroups?.forEach((rfc) => {
      rfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        skipColumns.push(..._ids);
      });
    });

    const h1SkipColumns: string[] = [];
    _h1LeftFixedColumnGroups?.forEach((lfc) => {
      lfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h1SkipColumns.push(..._ids);
      });
    });
    _h1BodyColumnGroups?.forEach((bc) => {
      bc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h1SkipColumns.push(..._ids);
      });
    });
    _h1RightFixedColumnGroups?.forEach((rfc) => {
      rfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h1SkipColumns.push(..._ids);
      });
    });

    const h2SkipColumns: string[] = [];
    _h2LeftFixedColumnGroups?.forEach((lfc) => {
      lfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h2SkipColumns.push(..._ids);
      });
    });
    _h2BodyColumnGroups?.forEach((bc) => {
      bc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h2SkipColumns.push(..._ids);
      });
    });
    _h2RightFixedColumnGroups?.forEach((rfc) => {
      rfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h2SkipColumns.push(..._ids);
      });
    });

    const h3SkipColumns: string[] = [];
    _h3LeftFixedColumnGroups?.forEach((lfc) => {
      lfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h3SkipColumns.push(..._ids);
      });
    });
    _h3BodyColumnGroups?.forEach((bc) => {
      bc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h3SkipColumns.push(..._ids);
      });
    });
    _h3RightFixedColumnGroups?.forEach((rfc) => {
      rfc.forEach((c) => {
        const _ids = c.itemIds.filter((i, _index) => _index !== 0);
        h3SkipColumns.push(..._ids);
      });
    });

    const _groupColumns: AnyColumn[] = [];
    const _h1GroupColumns: AnyColumn[] = [];
    const _h2GroupColumns: AnyColumn[] = [];
    const _h3GroupColumns: AnyColumn[] = [];
    _columns.forEach((c) => {
      if (skipColumns.includes(c.itemId)) {
        return;
      }
      let matchFound = false;
      _leftFixedColumnGroups?.forEach((lfc) => {
        const found = lfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _groupColumns.push(c);
        }
      });
      _bodyColumnGroups?.forEach((bc) => {
        const found = bc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _groupColumns.push(c);
        }
      });
      _rightFixedColumnGroups?.forEach((rfc) => {
        const found = rfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _groupColumns.push(c);
        }
      });
      if (!matchFound) {
        _groupColumns.push(c);
      }
    });

    _columns.forEach((c) => {
      if (h1SkipColumns.includes(c.itemId)) {
        return;
      }
      let matchFound = false;
      _h1LeftFixedColumnGroups?.forEach((lfc) => {
        const found = lfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h1GroupColumns.push(c);
        }
      });
      _h1BodyColumnGroups?.forEach((bc) => {
        const found = bc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h1GroupColumns.push(c);
        }
      });
      _h1RightFixedColumnGroups?.forEach((rfc) => {
        const found = rfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h1GroupColumns.push(c);
        }
      });
      if (!matchFound) {
        _h1GroupColumns.push(c);
      }
    });

    _columns.forEach((c) => {
      if (h2SkipColumns.includes(c.itemId)) {
        return;
      }
      let matchFound = false;
      _h2LeftFixedColumnGroups?.forEach((lfc) => {
        const found = lfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h2GroupColumns.push(c);
        }
      });
      _h2BodyColumnGroups?.forEach((bc) => {
        const found = bc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h2GroupColumns.push(c);
        }
      });
      _h2RightFixedColumnGroups?.forEach((rfc) => {
        const found = rfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h2GroupColumns.push(c);
        }
      });
      if (!matchFound) {
        _h2GroupColumns.push(c);
      }
    });

    _columns.forEach((c) => {
      if (h3SkipColumns.includes(c.itemId)) {
        return;
      }
      let matchFound = false;
      _h3LeftFixedColumnGroups?.forEach((lfc) => {
        const found = lfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h3GroupColumns.push(c);
        }
      });
      _h3BodyColumnGroups?.forEach((bc) => {
        const found = bc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h3GroupColumns.push(c);
        }
      });
      _h3RightFixedColumnGroups?.forEach((rfc) => {
        const found = rfc.find((_c) => _c.itemIds.includes(c.itemId));
        if (found) {
          matchFound = true;
          _h3GroupColumns.push(c);
        }
      });
      if (!matchFound) {
        _h3GroupColumns.push(c);
      }
    });

    const __h1GroupColumns = _h1GroupColumns;
    const h1HasPinLeft = __h1GroupColumns.some((c) => c.props.pinLeft);
    const _h1LeftColumns = h1HasPinLeft
      ? __h1GroupColumns.filter(
          (c) =>
            c.props.pinLeft ||
            ['RowNumberColumn', 'RowSelectionColumn'].includes(c.type),
        )
      : EMPTY_ARRAY;
    const h1HasPinRight = __h1GroupColumns.some((c) => c.props.pinRight);
    const _h1RightColumns = h1HasPinRight
      ? __h1GroupColumns.filter((c) => c.props.pinRight)
      : EMPTY_ARRAY;
    const _h1BodyColumns =
      h1HasPinLeft || h1HasPinRight
        ? __h1GroupColumns.filter(
            (c) => !(_h1LeftColumns.includes(c) || c.props.pinRight),
          )
        : __h1GroupColumns;

    const __h2GroupColumns = _h2GroupColumns;
    const h2HasPinLeft = __h2GroupColumns.some((c) => c.props.pinLeft);
    const _h2LeftColumns = h2HasPinLeft
      ? __h2GroupColumns.filter(
          (c) =>
            c.props.pinLeft ||
            ['RowNumberColumn', 'RowSelectionColumn'].includes(c.type),
        )
      : EMPTY_ARRAY;
    const h2HasPinRight = __h2GroupColumns.some((c) => c.props.pinRight);
    const _h2RightColumns = h2HasPinRight
      ? __h2GroupColumns.filter((c) => c.props.pinRight)
      : EMPTY_ARRAY;
    const _h2BodyColumns =
      h2HasPinLeft || h2HasPinRight
        ? __h2GroupColumns.filter(
            (c) => !(_h2LeftColumns.includes(c) || c.props.pinRight),
          )
        : __h2GroupColumns;

    const __h3GroupColumns = _h3GroupColumns;
    const h3HasPinLeft = __h3GroupColumns.some((c) => c.props.pinLeft);
    const _h3LeftColumns = h3HasPinLeft
      ? __h3GroupColumns.filter(
          (c) =>
            c.props.pinLeft ||
            ['RowNumberColumn', 'RowSelectionColumn'].includes(c.type),
        )
      : EMPTY_ARRAY;
    const h3HasPinRight = __h3GroupColumns.some((c) => c.props.pinRight);
    const _h3RightColumns = h3HasPinRight
      ? __h3GroupColumns.filter((c) => c.props.pinRight)
      : EMPTY_ARRAY;
    const _h3BodyColumns =
      h3HasPinLeft || h3HasPinRight
        ? __h3GroupColumns.filter(
            (c) => !(_h3LeftColumns.includes(c) || c.props.pinRight),
          )
        : __h3GroupColumns;

    const __groupColumns = _groupColumns;
    const hasGroupPinLeft = __groupColumns.some((c) => c.props.pinLeft);
    const _leftGroupColumns = hasGroupPinLeft
      ? __groupColumns.filter(
          (c) =>
            c.props.pinLeft ||
            ['RowNumberColumn', 'RowSelectionColumn'].includes(c.type),
        )
      : EMPTY_ARRAY;
    const hasGroupPinRight = __groupColumns.some((c) => c.props.pinRight);
    const _rightGroupColumns = hasGroupPinRight
      ? __groupColumns.filter((c) => c.props.pinRight)
      : EMPTY_ARRAY;
    const _bodyGroupColumns =
      hasGroupPinLeft || hasGroupPinRight
        ? __groupColumns.filter(
            (c) => !(_leftColumns.includes(c) || c.props.pinRight),
          )
        : __groupColumns;

    return {
      columns: _columns,
      leftColumns: _leftColumns,
      rightColumns: _rightColumns,
      bodyColumns,
      h1LeftColumns: _h1LeftColumns,
      h1RightColumns: _h1RightColumns,
      h1BodyColumns: _h1BodyColumns,
      h2LeftColumns: _h2LeftColumns,
      h2RightColumns: _h2RightColumns,
      h2BodyColumns: _h2BodyColumns,
      h3LeftColumns: _h3LeftColumns,
      h3RightColumns: _h3RightColumns,
      h3BodyColumns: _h3BodyColumns,
      ids: _columns.map((c) => c.itemId),
      footerRows: _footerRows,
      leftFixedColumnGroups: _leftFixedColumnGroups,
      bodyColumnGroups: _bodyColumnGroups,
      rightFixedColumnGroups: _rightFixedColumnGroups,
      h1LeftFixedColumnGroups: _h1LeftFixedColumnGroups,
      h1BodyColumnGroups: _h1BodyColumnGroups,
      h1RightFixedColumnGroups: _h1RightFixedColumnGroups,
      h2LeftFixedColumnGroups: _h2LeftFixedColumnGroups,
      h2BodyColumnGroups: _h2BodyColumnGroups,
      h2RightFixedColumnGroups: _h2RightFixedColumnGroups,
      h3LeftFixedColumnGroups: _h3LeftFixedColumnGroups,
      h3BodyColumnGroups: _h3BodyColumnGroups,
      h3RightFixedColumnGroups: _h3RightFixedColumnGroups,
      groupColumns: _groupColumns,
      h1GroupColumns: _h1GroupColumns,
      h2GroupColumns: _h2GroupColumns,
      h3GroupColumns: _h3GroupColumns,
      leftGroupColumns: _leftGroupColumns,
      rightGroupColumns: _rightGroupColumns,
      bodyGroupColumns: _bodyGroupColumns,
    };
  }, [
    appUid,
    initColumnGroups,
    itemId,
    items,
    pageId,
    parentItem?.type,
    platform,
  ]);

  const treeColumn = columns.find((c) => c.type === 'TreeColumn') as
    | TreeColumnItem
    | undefined;

  useTreeState(
    treeColumn?.props.nodeIdAttribute ?? '',
    treeColumn?.props.parentNodeIdAttribute ?? '',
    treeColumn?.props.initialExpanded ?? false,
  );

  return {
    columns,
    leftColumns,
    rightColumns,
    ids,
    isQueried,
    footerRows,
    leftFixedColumnGroups,
    bodyColumnGroups,
    rightFixedColumnGroups,
    groupColumns,
    h1GroupColumns,
    h2GroupColumns,
    h3GroupColumns,
    leftGroupColumns,
    rightGroupColumns,
    bodyGroupColumns,
    h1LeftFixedColumnGroups,
    h1BodyColumnGroups,
    h1RightFixedColumnGroups,
    h2LeftFixedColumnGroups,
    h2BodyColumnGroups,
    h2RightFixedColumnGroups,
    h3LeftFixedColumnGroups,
    h3BodyColumnGroups,
    h3RightFixedColumnGroups,
    h1LeftColumns,
    h1RightColumns,
    h1BodyColumns,
    h2LeftColumns,
    h2RightColumns,
    h2BodyColumns,
    h3LeftColumns,
    h3RightColumns,
    h3BodyColumns,
  };
};

export interface FormulaColumnsRecord {
  orgUid: string;
  appUid: string;
  pageUid: string;
  parentId: string;
  runtimeChildren: string[];
  runtimeItems: Record<string, AnyFormulaColumn>;
  userName: string;
  runtimeProps: Record<string, RunTimeProps>;
}

const removeObj = <T>(o: Record<string, T>, key: string): Record<string, T> => {
  const newObj: Record<string, T> = {};
  Object.keys(o)
    .filter((k) => k !== key)
    .forEach((k) => {
      newObj[k] = o[k];
    });
  return newObj;
};

export const useFormulaColumns = (
  appUid: string,
  pageId: string,
  itemId: string,
) => {
  const devMode = useDeveloperMode(appUid, pageId);
  const platform = usePlatform();
  const enableFormulaColumn =
    platform.getDesignProp<DataGrid.DataGrid.DataGrid>(
      appUid,
      pageId,
      itemId,
      'enableFormulaColumn',
    );
  const [isQueried, setIsQueried] = useState(!enableFormulaColumn);
  const { store, records, recordsOrder } = useAppStore<FormulaColumnsRecord>({
    appUid,
    pageId,
    ds: 'IoRuntimeItems',
    alias: enableFormulaColumn ? `IoRuntimeItems-${itemId}` : '$',
  });
  useEffect(() => {
    if (!enableFormulaColumn) {
      return;
    }
    store
      .query({
        props: {
          filter: [
            { parentId: { is: itemId } },
            { appUid: { is: appUid } },
            { pageUid: { is: pageId } },
          ],
        },
      })
      .finally(() => {
        setIsQueried(true);
      });
  }, [
    appUid,
    enableFormulaColumn,
    itemId,
    pageId,
    platform,
    setIsQueried,
    store,
  ]);

  return {
    formulaColumnsRecord: devMode ? undefined : records[recordsOrder[0]],
    store,
    isQueried,
  };
};

export const useFormulaColumnsEditor = (
  appUid: string,
  pageId: string,
  itemId: string,
) => {
  const { formulaColumnsRecord, store } = useFormulaColumns(
    appUid,
    pageId,
    itemId,
  );
  const smartSeachItemId = useStateXAtomValue(gridSmartSearchAtom, {
    params: { appUid, pageId, itemId },
  });
  const get = useStateXGetter();
  const [runtimeChildren] = getChildren(appUid, pageId, itemId, get);

  const updateFormulaRecord = async (id: string, column: AnyFormulaColumn) => {
    if (formulaColumnsRecord) {
      const formulaColumns = formulaColumnsRecord.runtimeItems;
      const col = formulaColumns?.[id];
      if (col) {
        store.updateRecord(formulaColumnsRecord._id ?? '', {
          runtimeItems: { ...formulaColumns, [id]: column },
        });
        await store.save();
        store.clearPreviousRequestCache();
      }
    }
  };

  const createFormulaRecord = async (
    column: AnyFormulaColumn,
    prevItemId: string,
    set: StateXSetter,
  ) => {
    const id = column.itemId;
    const prevItemIndex = runtimeChildren.indexOf(prevItemId);
    const newRuntimeChildren = [
      ...runtimeChildren.slice(0, prevItemIndex + 1),
      id,
      ...runtimeChildren.slice(prevItemIndex + 1),
    ];
    if (formulaColumnsRecord) {
      const formulaColumns = formulaColumnsRecord.runtimeItems;

      const col = formulaColumns?.[id];
      if (!col && formulaColumnsRecord._id) {
        store.updateRecord(formulaColumnsRecord._id, {
          runtimeItems: { ...formulaColumns, [id]: column },
          runtimeChildren: newRuntimeChildren,
          parentId: column.parent,
        });
      }
    } else {
      store.createNew({
        partialRecord: {
          appUid,
          runtimeItems: { [id]: column },
          runtimeChildren: newRuntimeChildren,
          pageUid: pageId,
          parentId: column.parent,
          runtimeProps: {},
        },
        rs: 'I',
      });
    }

    await store.save();
    if (smartSeachItemId) {
      set(smartSearchChildrenRTAtom, newRuntimeChildren, {
        params: { appUid, pageId, itemId },
      });
      set(smartSearchUpdateAtom, true, {
        params: { appUid, pageId, itemId: smartSeachItemId },
      });
    }

    set(childrenRTAtom, newRuntimeChildren, {
      params: { appUid, pageId, itemId },
    });
    store.clearPreviousRequestCache();
  };

  return {
    updateRecord: updateFormulaRecord,
    createNew: createFormulaRecord,
  };
};

export const useFormulaColumnRemover = (
  appUid: string,
  pageId: string,
  itemId: string,
) => {
  const set = useStateXSetter();
  const get = useStateXGetter();
  const { formulaColumnsRecord, store } = useFormulaColumns(
    appUid,
    pageId,
    itemId,
  );
  const [runtimeChildren] = getChildren(appUid, pageId, itemId, get);
  const smartSeachItemId = useStateXAtomValue(gridSmartSearchAtom, {
    params: { appUid, pageId, itemId },
  });
  const removeRecord = async (id: string) => {
    if (formulaColumnsRecord) {
      let formulaColumns = formulaColumnsRecord.runtimeItems;
      const col = formulaColumns?.[id];
      if (col) {
        formulaColumns = removeObj<AnyFormulaColumn>(formulaColumns ?? {}, id);
        if (Object.keys(formulaColumns ?? {}).length === 0) {
          await store.deleteRecord(formulaColumnsRecord._id ?? '');
        }
        store.updateRecord(formulaColumnsRecord._id ?? '', {
          runtimeItems: formulaColumns,
          runtimeChildren: (formulaColumnsRecord?.runtimeChildren ?? []).filter(
            (cid) => cid !== id,
          ),
        });
        await store.save();
        const newRuntimeChildren = runtimeChildren.filter((cid) => cid !== id);

        if (smartSeachItemId) {
          set(smartSearchChildrenRTAtom, newRuntimeChildren, {
            params: { appUid, pageId, itemId },
          });
          set(smartSearchUpdateAtom, true, {
            params: { appUid, pageId, itemId: smartSeachItemId },
          });
        }
        set(childrenRTAtom, newRuntimeChildren, {
          params: { appUid, pageId, itemId },
        });
        store.clearPreviousRequestCache();
      }
    }
  };

  return {
    deleteRecord: removeRecord,
  };
};

export const useColumnsRuntimeData = (
  appUid: string,
  pageId: string,
  itemId: string,
) => {
  const { formulaColumnsRecord, store } = useFormulaColumns(
    appUid,
    pageId,
    itemId,
  );
  const { children: visibleChildren } = useVisibleChildren(
    appUid,
    pageId,
    itemId,
  );
  const smartSeachItemId = useStateXAtomValue(gridSmartSearchAtom, {
    params: { appUid, pageId, itemId },
  });
  const get = useStateXGetter();
  const set = useStateXSetter();
  const [runtimeChildren] = getChildren(appUid, pageId, itemId, get);

  const updateRuntimeProps = async (id: string, props: RunTimeProps) => {
    if (formulaColumnsRecord) {
      store.updateRecord(formulaColumnsRecord._id ?? '', {
        runtimeProps: { ...formulaColumnsRecord.runtimeProps, [id]: props },
      });
    } else {
      await store.createNew({
        partialRecord: {
          runtimeItems: {},
          runtimeChildren,
          parentId: itemId,
          runtimeProps: { [id]: props },
        },
        rs: 'I',
      });
    }
    await store.save();
    store.clearPreviousRequestCache();
  };

  const reorderColumns = (startIndex: number, endIndex: number) => {
    const children = [...getChildren(appUid, pageId, itemId, get)[0]];
    const actualStartIndex = children.indexOf(visibleChildren[startIndex]);
    let actualEndIndex = children.indexOf(visibleChildren[endIndex]);
    if (actualEndIndex > actualStartIndex) {
      actualEndIndex -= 1;
    }
    children.splice(actualEndIndex, 0, children.splice(actualStartIndex, 1)[0]);
    if (smartSeachItemId) {
      set(smartSearchChildrenRTAtom, children, {
        params: { appUid, pageId, itemId },
      });
      set(smartSearchUpdateAtom, true, {
        params: { appUid, pageId, itemId: smartSeachItemId },
      });
    }
    set(childrenRTAtom, children, { params: { appUid, pageId, itemId } });
  };

  return {
    updateRuntimeProps,
    reorderColumns,
  };
};
